<template>
  <div
    class="border rounded-lg p-4 transition-all duration-200"
    :class="cardClasses">
    <!-- Status Badge and Actions -->
    <div class="flex justify-between items-start mb-3">
      <div class="flex items-center">
        <span
          class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
          :class="statusBadgeClasses">
          <i
            :class="statusIconClass"
            class="mr-1" />
          {{ statusLabel }}
        </span>
      </div>
      <div class="flex gap-2">
        <button
          :disabled="checking"
          class="p-1.5 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          title="Check Now"
          @click.stop="checkNow">
          <i :class="checking ? 'fas fa-spinner fa-spin' : 'fas fa-sync'" />
        </button>
        <div class="relative">
          <button
            class="p-1.5 text-gray-500 hover:text-gray-700 transition-colors"
            @click.stop="showMenu = !showMenu">
            <i class="fas fa-ellipsis-v" />
          </button>
          <div
            v-if="showMenu"
            v-click-outside="() => showMenu = false"
            class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border">
            <button
              class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              @click.stop="editPage">
              <i class="fas fa-edit mr-2" />
              Edit
            </button>
            <button
              class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50"
              @click.stop="deletePage">
              <i class="fas fa-trash mr-2" />
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- URL and Type -->
    <div class="mb-3">
      <a
        :href="page.url"
        target="_blank"
        rel="noopener noreferrer"
        class="text-blue-600 hover:underline text-sm break-all">
        {{ page.url }}
      </a>
      <span
        v-if="page.page_type"
        class="ml-2 inline-flex items-center px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded">
        {{ formatPageType(page.page_type) }}
      </span>
    </div>

    <!-- Metadata -->
    <div class="grid grid-cols-2 gap-2 text-sm text-gray-600">
      <div
        v-if="page.geo"
        class="flex items-center">
        <i class="fas fa-map-marker-alt mr-1 text-gray-400" />
        <span class="truncate">{{ page.geo }}</span>
      </div>
      <div
        v-if="page.category"
        class="flex items-center">
        <i class="fas fa-tag mr-1 text-gray-400" />
        <span class="truncate">{{ page.category }}</span>
      </div>
    </div>

    <!-- Installation Info -->
    <div
      v-if="page.widget_installed"
      class="mt-3 pt-3 border-t border-gray-200">
      <p class="text-xs text-green-600 flex items-center">
        <i class="fas fa-check-circle mr-1" />
        Installed for <span class="font-semibold mx-1">{{ page.installation_days || 0 }}</span> days
      </p>
    </div>

    <!-- Error Info -->
    <div
      v-if="page.last_check_status === 'error'"
      class="mt-3 pt-3 border-t border-gray-200">
      <p class="text-xs text-red-600">
        <span v-if="page.last_check_http_code">
          HTTP {{ page.last_check_http_code }}:
        </span>
        {{ page.last_check_error || 'Unknown error' }}
      </p>
    </div>

    <!-- Last Check -->
    <div class="mt-3 text-xs text-gray-500">
      <span v-if="page.last_check_at">
        Last checked: {{ formatDate(page.last_check_at) }}
      </span>
      <span v-else>
        Never checked
      </span>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  page: {
    type: Object,
    required: true
  }
})

const emit = defineEmits(['check-now', 'edit', 'delete'])

// Data
const checking = ref(false)
const showMenu = ref(false)

// Computed
const cardClasses = computed(() => {
  const status = props.page.last_check_status
  if (status === 'checking') return 'border-blue-300 bg-blue-50'
  if (status === 'installed' || props.page.widget_installed) return 'border-green-300 bg-green-50'
  if (status === 'error') return 'border-red-300 bg-red-50'
  if (status === 'not_installed') return 'border-yellow-300 bg-yellow-50'
  return 'border-gray-300'
})

const statusBadgeClasses = computed(() => {
  const status = props.page.last_check_status
  if (status === 'checking') return 'bg-blue-100 text-blue-800'
  if (status === 'installed' || props.page.widget_installed) return 'bg-green-100 text-green-800'
  if (status === 'error') return 'bg-red-100 text-red-800'
  if (status === 'not_installed') return 'bg-yellow-100 text-yellow-800'
  return 'bg-gray-100 text-gray-800'
})

const statusIconClass = computed(() => {
  const status = props.page.last_check_status
  if (status === 'checking') return 'fas fa-spinner fa-spin'
  if (status === 'installed' || props.page.widget_installed) return 'fas fa-check-circle'
  if (status === 'error') return 'fas fa-exclamation-triangle'
  if (status === 'not_installed') return 'fas fa-times-circle'
  return 'fas fa-question-circle'
})

const statusLabel = computed(() => {
  const status = props.page.last_check_status
  if (status === 'checking') return 'Checking...'
  if (status === 'installed' || props.page.widget_installed) return 'Installed'
  if (status === 'error') return 'Error'
  if (status === 'not_installed') return 'Not Installed'
  return 'Not Checked'
})

// Methods
const checkNow = async() => {
  if (checking.value) return

  checking.value = true
  emit('check-now', props.page.id)

  // Reset after 2 seconds if no update received
  setTimeout(() => {
    checking.value = false
  }, 2000)
}

const editPage = () => {
  showMenu.value = false
  emit('edit', props.page)
}

const deletePage = () => {
  showMenu.value = false
  emit('delete', props.page.id)
}

const formatPageType = (type) => {
  const types = {
    home: 'Home',
    location: 'Location',
    service: 'Service',
    'location-service': 'Location Service'
  }
  return types[type] || type
}

const formatDate = (dateString) => {
  if (!dateString) return 'Never'

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now - date
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`
  if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
  })
}

// Click outside directive (simple implementation)
const vClickOutside = {
  mounted(el, binding) {
    el.clickOutsideEvent = (event) => {
      if (!(el === event.target || el.contains(event.target))) {
        binding.value()
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el) {
    document.removeEventListener('click', el.clickOutsideEvent)
  }
}
</script>
